import type { DestinationTranslationValue } from '@/shared/api/types/destination-types'
import { useTranslation } from 'react-i18next'
import { DestinationTranslationSection } from '@/features/destination'
import { cn } from '@/shared/libs/cn'
import { FormSectionCard } from '@/shared/ui/form-section-card'
import { SectionSaveButton } from '@/shared/ui/section-save-button'

interface TranslationsCardProps {
  isEdit: boolean
  isReadonly: boolean
  /** The destination or the tenant languages are still loading. */
  isLoading: boolean
  languages: string[]
  translations: DestinationTranslationValue[]
  onChange: (value: DestinationTranslationValue[]) => void
  /** Id the translation jobs are filed under (destination id, or the shared draft id when creating). */
  entityId: string
  runningJobCount: number
  isSaving: boolean
  isSaveDisabled: boolean
  onSave: () => void
}

/** Title, name and description of the destination in every language, with AI translation. */
export function TranslationsCard({
  isEdit,
  isReadonly,
  isLoading,
  languages,
  translations,
  onChange,
  entityId,
  runningJobCount,
  isSaving,
  isSaveDisabled,
  onSave,
}: TranslationsCardProps) {
  const { t } = useTranslation()
  const entityLabel =
    translations.find((item) => item.name?.trim())?.name ??
    t('sidebar.destinations', { defaultValue: 'Destinations' })
  const entityPath = typeof window === 'undefined' ? undefined : window.location.pathname

  return (
    <FormSectionCard
      title={t('tour.translations')}
      action={
        isEdit &&
        !isReadonly && (
          <SectionSaveButton disabled={isSaveDisabled} saving={isSaving} onSave={onSave} />
        )
      }
    >
      <div className={cn(isLoading && 'opacity-60 pointer-events-none')}>
        {runningJobCount > 0 && (
          <p className="mb-3 text-sm text-blue-700">
            {t('jobs.runningCount', { count: runningJobCount })}
          </p>
        )}
        <DestinationTranslationSection
          languages={languages}
          translations={translations}
          onChange={onChange}
          entityId={entityId}
          entityLabel={entityLabel}
          readonly={isReadonly}
          {...(entityPath ? { entityPath } : {})}
        />
      </div>
    </FormSectionCard>
  )
}
